import type { HourlyPeakLoad, RequestBucket, ServiceDay, ShiftSummary } from '../../../../../services/simulation/runReport';

/** Share of seats taken, rounded up to the nearest quarter so one passenger never reads as empty; 0 is running empty. */
export type OccupancyLevel = 0 | 0.25 | 0.5 | 0.75 | 1;

export interface GanttSegment {
  readonly startMs: number;
  readonly endMs: number;
  readonly leftPct: number;
  readonly widthPct: number;
  readonly aboard: number;
  readonly seats: number;
  readonly level: OccupancyLevel;
}

export interface GanttRow {
  readonly shift: ShiftSummary;
  readonly shiftLeftPct: number;
  readonly shiftWidthPct: number;
  readonly segments: readonly GanttSegment[];
  readonly pickPcts: readonly number[];
  readonly dropPcts: readonly number[];
}

export interface HistogramBin {
  readonly start: number;
  readonly end: number;
  readonly count: number;
}

export interface Histogram {
  readonly bins: readonly HistogramBin[];
  /** Decimal places that tell neighbouring bin edges apart. */
  readonly edgeDigits: number;
}

const HOUR_MS = 3_600_000;
const NICE_STEPS = [1, 2, 5] as const;
const LEVELS: readonly OccupancyLevel[] = [0, 0.25, 0.5, 0.75, 1];

export function occupancyLevel(aboard: number, seats: number): OccupancyLevel {
  if (aboard <= 0 || seats <= 0) return 0;
  return LEVELS[Math.min(4, Math.ceil((aboard / seats) * 4))];
}

const clampPct = (value: number) => Math.min(100, Math.max(0, value));

function pct(ms: number, day: ServiceDay): number {
  return ((ms - day.startMs) / (day.endMs - day.startMs)) * 100;
}

export function ganttRows(shifts: readonly ShiftSummary[], day: ServiceDay): GanttRow[] {
  return shifts
    .filter((shift) => shift.trips > 0)
    .map((shift) => {
      const seats = shift.seats.standard + shift.seats.wheelchair;
      return {
        shift,
        shiftLeftPct: pct(shift.startMs, day),
        shiftWidthPct: pct(shift.endMs, day) - pct(shift.startMs, day),
        segments: shift.segments.map((segment) => {
          const aboard = segment.aboard.standard + segment.aboard.wheelchair;
          return {
            startMs: segment.startMs,
            endMs: segment.endMs,
            leftPct: pct(segment.startMs, day),
            widthPct: pct(segment.endMs, day) - pct(segment.startMs, day),
            aboard,
            seats,
            level: occupancyLevel(aboard, seats),
          };
        }),
        pickPcts: shift.stops.filter((stop) => stop.kind === 'pick').map((stop) => clampPct(pct(stop.atMs, day))),
        dropPcts: shift.stops.filter((stop) => stop.kind === 'drop').map((stop) => clampPct(pct(stop.atMs, day))),
      };
    });
}

/** The service day widened to whole hours, so hourly buckets line up with the hour labels. */
export function wholeHourDay(day: ServiceDay): ServiceDay {
  return { startMs: Math.floor(day.startMs / HOUR_MS) * HOUR_MS, endMs: Math.ceil(day.endMs / HOUR_MS) * HOUR_MS };
}

export function hourTicks(day: ServiceDay, stepHours: number): Array<{ readonly ms: number; readonly pct: number }> {
  const first = Math.ceil(day.startMs / HOUR_MS) * HOUR_MS;
  const ticks: Array<{ ms: number; pct: number }> = [];
  for (let ms = first; ms <= day.endMs; ms += stepHours * HOUR_MS) ticks.push({ ms, pct: pct(ms, day) });
  return ticks;
}

export function niceBinWidth(max: number, targetBins: number): number {
  if (!(max > 0)) return 1;
  const raw = max / targetBins;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = NICE_STEPS.find((candidate) => candidate * magnitude >= raw) ?? 10;
  return step * magnitude;
}

/**
 * Ignores values that are not finite numbers; values below zero count in the first bin.
 * `span` sets the bin edges, so two runs binned over both their values share edges.
 */
export function histogram(values: readonly number[], targetBins: number, span: readonly number[] = values): Histogram {
  const finite = values.filter(Number.isFinite);
  const spanned = span.filter(Number.isFinite);
  if (spanned.length === 0) return { bins: [], edgeDigits: 0 };
  const max = spanned.reduce((highest, value) => Math.max(highest, value), 0);
  const width = niceBinWidth(max, targetBins);
  const edgeDigits = Math.max(0, -Math.floor(Math.log10(width)));
  const binCount = Math.max(1, Math.ceil(max / width));
  const bins = Array.from({ length: binCount }, (_, i) => ({
    start: Number((i * width).toFixed(edgeDigits)),
    end: Number(((i + 1) * width).toFixed(edgeDigits)),
    count: 0,
  }));
  for (const value of finite) bins[Math.min(binCount - 1, Math.floor(Math.max(0, value) / width))].count += 1;
  return { bins, edgeDigits };
}

export function busiestHour(buckets: readonly RequestBucket[]): RequestBucket | null {
  const total = (bucket: RequestBucket) => bucket.matched + bucket.unmatched;
  const busiest = buckets.reduce<RequestBucket | null>((best, bucket) => (best == null || total(bucket) > total(best) ? bucket : best), null);
  return busiest && total(busiest) > 0 ? busiest : null;
}

export function fullestHour(hours: readonly HourlyPeakLoad[]): HourlyPeakLoad | null {
  const fullest = hours.reduce<HourlyPeakLoad | null>(
    (best, hour) => (hour.standard != null && (best?.standard == null || hour.standard > best.standard) ? hour : best),
    null
  );
  return fullest?.standard ? fullest : null;
}

const DAY_MS = 24 * HOUR_MS;

/** Hours since midnight UTC of the run's own date, so runs on different dates line up by time of day. */
export function hourOfDay(ms: number, dayStartMs: number): number {
  return Math.round((ms - Math.floor(dayStartMs / DAY_MS) * DAY_MS) / HOUR_MS);
}

export interface HourlyRun<T> {
  readonly buckets: readonly T[];
  readonly dayStartMs: number;
}

/** Every hour of day from the earliest to the latest bucket across the runs. */
export function hourSpan(runs: ReadonlyArray<HourlyRun<{ readonly startMs: number }>>): number[] {
  const hours = runs.flatMap((run) => run.buckets.map((bucket) => hourOfDay(bucket.startMs, run.dayStartMs)));
  if (hours.length === 0) return [];
  const first = hours.reduce((low, hour) => Math.min(low, hour));
  const last = hours.reduce((high, hour) => Math.max(high, hour));
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

/** One value per hour of the span; null where the run has no bucket. */
export function onHours<T extends { readonly startMs: number }>(hours: readonly number[], run: HourlyRun<T>, value: (bucket: T) => number | null): (number | null)[] {
  const byHour = new Map(run.buckets.map((bucket) => [hourOfDay(bucket.startMs, run.dayStartMs), bucket]));
  return hours.map((hour) => {
    const bucket = byHour.get(hour);
    return bucket ? value(bucket) : null;
  });
}

export const hourLabel = (hour: number): string => String(hour % 24).padStart(2, '0');
